/**
 * HITLPrompt Component
 *
 * Renders human-in-the-loop requests in the timeline feed.
 * Option buttons are rendered inline. Free-text input is handled by the
 * chat input + HITLContextBar — this component no longer renders a text input.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Button } from '@agion/ui'
import { ShieldAlert, HeadsetIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { MarkdownContent } from './MarkdownContent'
import { composerText } from './composerTexts'
import { APPROVAL_ANSWERS, DecideInEscalations, approvalEscalationId, approvalMode, escalationsHref } from './governanceApproval'
import type { ActiveHITLRequest } from '../../hooks/useStreamingMissionChat'
import type { HITLDecision } from '../../types/streaming'
import { questionCountdown } from '../../utils/questionOptions'

interface HITLPromptProps {
  request: ActiveHITLRequest
  /** decision: only from an approval card decided inline (agion/Agion#2618). */
  onRespond: (requestId: string, response: string, decision?: HITLDecision) => void
  /**
   * False when the question is not open: answered, its answer on its way, or
   * its run ended. Its choices then cannot send and say why (phase 4,
   * agion/Agion#2327).
   */
  isOpen?: boolean
}

/** Resolve the effective kind, defaulting to user_prompt for backward compat */
function resolveKind(request: ActiveHITLRequest): NonNullable<ActiveHITLRequest['kind']> {
  if (request.kind) return request.kind
  if (request.governanceContext?.ruleName) return 'policy_review'
  return 'user_prompt'
}

/** Reusable countdown hook */
export function useHITLCountdown(receivedAt: number, timeoutMs?: number): { remainingMs: number | null; isExpired: boolean } {
  const [remainingMs, setRemainingMs] = useState<number | null>(() => {
    if (!timeoutMs) return null
    return Math.max(0, receivedAt + timeoutMs - Date.now())
  })
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    if (!timeoutMs) {
      setRemainingMs(null)
      setIsExpired(false)
      return
    }

    const endTime = receivedAt + timeoutMs
    const update = () => {
      const remaining = endTime - Date.now()
      if (remaining <= 0) {
        setIsExpired(true)
        setRemainingMs(0)
      } else {
        setRemainingMs(remaining)
      }
    }
    update()
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [receivedAt, timeoutMs])

  return { remainingMs, isExpired }
}

export const HITLPrompt: React.FC<HITLPromptProps> = ({ request, onRespond, isOpen = true }) => {
  const { t } = useTranslation(['common', 'chat'])
  const countdown = questionCountdown(request, isOpen)
  const { remainingMs, isExpired } = useHITLCountdown(countdown.from, countdown.lastsMs)
  const kind = resolveKind(request)
  const closedReason = isOpen ? undefined : composerText(t, 'questionClosed')
  // A governance approval is decided on the card only when its runner
  // decides it inline; otherwise in Escalations (agion/Agion#2618).
  const approval = approvalMode(request.governanceContext)

  const handleOptionClick = useCallback((option: string, decision?: HITLDecision) => {
    if (isExpired) return
    if (decision) {
      onRespond(request.requestId, option, decision)
      return
    }
    onRespond(request.requestId, option)
  }, [isExpired, onRespond, request.requestId])

  const formatRemaining = (ms: number): string => {
    const seconds = Math.ceil(ms / 1000)
    return `${seconds}s`
  }

  if (isExpired) {
    return (
      <p className="text-sm text-amber-600 dark:text-amber-400 italic">
        {t('common:hitl.expired', 'This request has expired.')}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {/* Kind-based header — operator handoff */}
      {(kind === 'operator_handoff' || kind === 'chat_handoff') && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 mb-1.5" data-testid="hitl-operator-header">
          <HeadsetIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span>
            {t('common:hitl.operatorHandoff.title', 'Agent needs operator help')}
          </span>
        </div>
      )}

      {/* Kind-based header — policy review */}
      {kind === 'policy_review' && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-300 mb-1.5" data-testid="hitl-policy-header">
          <ShieldAlert className="h-3.5 w-3.5 text-red-500 dark:text-red-400 flex-shrink-0" />
          <span>
            {t('common:hitl.policyReview.title', 'Governance review required')}
          </span>
        </div>
      )}

      {/* Governance context line — shown when a policy rule triggered this pause */}
      {request.governanceContext?.ruleName && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1.5">
          <ShieldAlert className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
          <span>
            {t('common:hitl.governanceRule', 'Governance rule: {{rule}}', {
              rule: request.governanceContext.ruleName,
            })}
          </span>
        </div>
      )}

      {/* Prompt text — amber bg for default/operator, red bg for policy review */}
      <div className={`rounded-md p-2.5 ${
        kind === 'policy_review'
          ? 'bg-red-50 dark:bg-red-500/10'
          : 'bg-amber-50 dark:bg-amber-500/10'
      }`}>
        <div className="text-sm text-slate-900 dark:text-white">
          <MarkdownContent content={request.prompt} />
        </div>
      </div>

      {/* Timeout indicator */}
      {remainingMs !== null && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {t('common:hitl.timeout', 'Expires in {{time}}', { time: formatRemaining(remainingMs) })}
        </div>
      )}

      {/* Governance approval decided inline: Approve and Deny send the explicit decision */}
      {approval === 'inline' && (
        <div className="flex flex-wrap gap-2">
          {APPROVAL_ANSWERS.map(({ option, decision }) => (
            <Button
              key={decision}
              variant="outline"
              size="sm"
              onClick={() => handleOptionClick(option, decision)}
              disabled={!isOpen}
              title={closedReason}
            >
              {decision === 'approved'
                ? t('common:hitl.policyReview.approve', 'Approve')
                : t('common:hitl.policyReview.deny', 'Deny')}
            </Button>
          ))}
        </div>
      )}

      {/* Governance approval from an older runner: decided in Escalations */}
      {approval === 'escalations' && (
        <DecideInEscalations href={escalationsHref(approvalEscalationId(request.governanceContext))} />
      )}

      {/* Options (for approval/choice) — inline buttons */}
      {approval === null && request.options && request.options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {request.options.map((option) => (
            <Button
              key={option}
              variant="outline"
              size="sm"
              onClick={() => handleOptionClick(option)}
              disabled={!isOpen}
              title={closedReason}
            >
              {option}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

export default HITLPrompt
